/**
 * The sidebar: every screen this reader may open, one click away.
 *
 * It replaces a round trip. `parentOf` returned `/` for all twenty-four flat `/ops/*` paths, so
 * moving between any two Ops screens cost two clicks through the landing page — and ten of those
 * screens were linked from nowhere at all. There was no `aria-current` anywhere in the
 * application, so a screen reader could not say where it was either.
 *
 * **Derived from `screens.ts`, not from a list here.** That is the whole point: a second list
 * would be the third statement of the same authority, and the two that already existed had
 * drifted. `reachableGroups` applies the guards' own rules, so the sidebar cannot offer a link
 * into a redirect — which `landing.tsx`'s header names as worse than offering nothing.
 *
 * **The drawer expands in flow rather than overlaying, and that is deliberate.** An overlay
 * needs a focus trap and a scrim, and a trap built here would be the second one in the codebase
 * — the first arrives with `<dialog>` in the forms slice, where `showModal()` gives it for free.
 * A drawer that pushes the page down traps nobody, needs no scrim, and cannot strand a keyboard
 * reader behind it. It still closes on Escape and on navigating, because a drawer left open over
 * the screen you just asked for is its own defect (the bell has neither, and should).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import type { SessionView } from '../api/client.js';
import { currentScreen, reachableGroups, reachableScreens } from './screens.js';

type LiveSession = Extract<SessionView, { authenticated: true }>;

export function Sidebar({ session }: { readonly session: LiveSession }): React.JSX.Element | null {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const groups = reachableGroups(session);
  const here = currentScreen(pathname, reachableScreens(session));

  // Close on navigating. Without this the drawer stays open over the screen it was used to reach.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /*
   * Nothing to show, so nothing is shown — rather than an empty rail taking a column of width.
   * Two readers reach this: a Merchant user is offered their two screens, but a person whose
   * invitation is unanswered holds nothing at all until they accept (ADR-0067, amended), and the
   * landing page renders only that invitation.
   */
  if (groups.length === 0) return null;

  return (
    <>
      {/*
        Hidden at desktop width by CSS rather than by a condition here, so there is no viewport
        measurement in JavaScript to disagree with the stylesheet about where the band is.
        `display: none` takes it out of the accessibility tree with it.
      */}
      <button
        type="button"
        className="btn side__toggle no-print"
        aria-expanded={open}
        aria-controls="side-nav"
        onClick={() => setOpen((was) => !was)}
      >
        {t('nav.sections')}
      </button>

      <nav
        id="side-nav"
        className="side no-print"
        aria-label={t('nav.sections')}
        data-open={open ? 'true' : 'false'}
      >
        {groups.map(({ group, screens }) => (
          <div className="side__group" key={group}>
            {/*
              A heading, not a bare label: the sidebar is a region a screen reader walks, and
              seven unlabelled lists are seven lists. `h2` sits under `PageHeader`'s `h1`.
            */}
            <h2 className="side__groupname">{t(`nav.group.${group}`)}</h2>
            <ul className="side__list">
              {screens.map((screen) => {
                const isHere = here?.screen.path === screen.path;
                return (
                  <li key={screen.path}>
                    <Link
                      className="side__link"
                      to={screen.path}
                      aria-current={isHere ? here.current : undefined}
                    >
                      {t(screen.navKey ?? screen.labelKey)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}
